import { Link } from 'gatsby';
import {
  ArrowRight,
  ExternalLink,
  Eye,
  MessageCircle,
  Sparkles,
  Terminal,
  Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { DEMO_CAMPAIGNS } from '../../constants/demo-copy';
import { AgentMockupChrome } from '../AppMockupChrome';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { Headline } from '../Headline';
import { mobileHeroLeadText } from '../MarketingTypography';
import { Reveal, usePrefersReducedMotion } from '../motion';
import { ProductIcon } from '../ProductIcon';

const SectionWrapper = styled('section')`
  position: relative;
  background: linear-gradient(
    180deg,
    ${COLORS.WHITE} 0%,
    ${COLORS.GRAY.surfaceTint} 10%,
    ${COLORS.GRAY.surfaceTint} 90%,
    ${COLORS.WHITE} 100%
  );
  background-color: ${COLORS.GRAY.surfaceTint};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.06) 1px,
      transparent 1px
    );
    background-size: 24px 24px;
    mask-image: radial-gradient(
      ellipse 75% 65% at 50% 40%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const Inner = styled('div')`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 88px 24px 80px;

  @media (max-width: 960px) {
    padding: 64px 24px 56px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 56px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: ${COLORS.GRAY.muted};
  margin-top: 16px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  ${mobileHeroLeadText}
`;

/* ------------------------------ Bento ----------------------------- */

const BentoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  /* Stretch reveal wrappers so tiles in a row share heights */
  > * {
    height: 100%;
    min-width: 0;
  }
`;

const Tile = styled('div')`
  position: relative;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 26px 24px;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const HeroCell = styled(Reveal)`
  grid-column: span 2;

  @media (max-width: 960px) {
    grid-column: auto;
  }
`;

const HeroTile = styled(Tile)`
  padding: 26px 28px;

  /* Light streak pulsing along the top edge */
  &::after {
    @keyframes topShimmer {
      0%,
      100% {
        opacity: 0.25;
      }
      50% {
        opacity: 0.9;
      }
    }

    content: '';
    position: absolute;
    top: -1px;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(46, 92, 255, 0.55),
      transparent
    );
    animation: topShimmer 3.6s ease-in-out infinite;
    pointer-events: none;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 0.5;
    }
  }
`;

const TileTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TileTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.INK};
  margin: 0;
`;

const TileDescription = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: ${COLORS.GRAY.muted};
  margin: 0;
  flex: 1;
`;

/* --------------------------- Agent demo --------------------------- */

const DemoWindow = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 12px;
  background: ${COLORS.GRAY.surface};
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(14, 28, 43, 0.05);
`;

const DemoBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  min-height: 264px;

  @media (max-width: 600px) {
    min-height: 300px;
  }
`;

const UserBubble = styled('div')`
  align-self: flex-end;
  max-width: 85%;
  padding: 9px 14px;
  border-radius: 12px 12px 3px 12px;
  background: ${COLORS.PAPER_DARK};
  border: 1px solid ${COLORS.GRAY.border};
  color: ${COLORS.INK};
  font-size: 13.5px;
  line-height: 1.5;
`;

const ThinkingRow = styled('div')<{ $visible: boolean }>`
  @keyframes thinkingDot {
    0%,
    80%,
    100% {
      opacity: 0.25;
    }
    40% {
      opacity: 1;
    }
  }

  display: ${(p) => (p.$visible ? 'flex' : 'none')};
  align-items: center;
  gap: 8px;
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  color: ${COLORS.GRAY.mutedLight};

  i {
    display: inline-flex;
    gap: 3px;
    font-style: normal;

    span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: ${COLORS.PRIMARY};
      animation: thinkingDot 1.2s ease-in-out infinite;

      &:nth-of-type(2) {
        animation-delay: 0.15s;
      }
      &:nth-of-type(3) {
        animation-delay: 0.3s;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }
`;

const Staged = styled('div')<{ $visible: boolean }>`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ThoughtLine = styled(Staged)`
  font-size: 12px;
  color: ${COLORS.GRAY.mutedLight};

  &::before {
    content: '‣ ';
  }
`;

const InlineCode = styled('code')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  color: ${COLORS.PRIMARY};
  background: ${COLORS.PRIMARY_SURFACE};
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap;
`;

const AnswerIntro = styled(Staged)`
  font-size: 13px;
  line-height: 1.55;
  font-weight: 600;
  color: ${COLORS.INK};
`;

const SectionLabel = styled('div')`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.GRAY.mutedLight};
`;

const FindingsList = styled('ul')`
  margin: 0;
  padding: 0 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FindingItem = styled('li')<{ $visible: boolean }>`
  font-size: 12.5px;
  line-height: 1.55;
  color: ${COLORS.GRAY.muted};
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  &::marker {
    color: ${COLORS.GRAY.mutedLight};
  }
`;

const RecommendationBox = styled(Staged)`
  border-left: 3px solid ${COLORS.PRIMARY};
  border-radius: 6px;
  background: ${COLORS.PRIMARY_SURFACE};
  padding: 10px 14px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${COLORS.INK};

  strong {
    display: block;
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${COLORS.PRIMARY};
  }
`;

const AnswerFooter = styled(Staged)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SourcesRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SourcePill = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  background: ${COLORS.PRIMARY_SURFACE};
  border-radius: 100px;
  padding: 4px 12px;
`;

const ActionButton = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 100px;
  background: ${COLORS.PRIMARY};
  color: ${COLORS.WHITE};
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const DEMO_QUESTION =
  'Which sponsorships are behind pace before Friday\u2019s client check-in?';

/**
 * Answer phases, mirroring the real agent UI: 0 idle, 1 thinking,
 * 2 thought line + intro, 3 findings, 4 second finding, 5 recommendation,
 * 6 sources + follow-ups.
 */
const AgentDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState(reduced ? 5 : 0);

  useEffect(() => {
    if (reduced) {
      setPhase(5);
      return;
    }
    const timers = [
      window.setTimeout(() => setPhase(1), 650),
      window.setTimeout(() => setPhase(2), 2200),
      window.setTimeout(() => setPhase(3), 2700),
      window.setTimeout(() => setPhase(4), 3300),
      window.setTimeout(() => setPhase(5), 3900)
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [reduced]);

  return (
    <DemoWindow>
      <AgentMockupChrome status="live data" />
      <DemoBody>
        <UserBubble>{DEMO_QUESTION}</UserBubble>
        <ThinkingRow $visible={phase === 1} aria-hidden>
          <i>
            <span />
            <span />
            <span />
          </i>
          checking campaign delivery…
        </ThinkingRow>
        <ThoughtLine $visible={phase >= 2}>Thought for 3 seconds</ThoughtLine>
        <AnswerIntro $visible={phase >= 2}>
          Two deals need attention before Friday.{' '}
          <InlineCode>{DEMO_CAMPAIGNS.homeRefresh}</InlineCode> has the most
          delivery risk.
        </AnswerIntro>
        <Staged $visible={phase >= 3}>
          <SectionLabel>Findings</SectionLabel>
        </Staged>
        <FindingsList>
          <FindingItem $visible={phase >= 3}>
            {DEMO_CAMPAIGNS.homeRefresh} is at 38% of goal with 6 days left in
            the flight. {DEMO_CAMPAIGNS.springAudio} is at 61% with 12 days
            remaining.
          </FindingItem>
        </FindingsList>
        <RecommendationBox $visible={phase >= 4}>
          <strong>Recommendation</strong>
          Review pacing on {DEMO_CAMPAIGNS.homeRefresh} while the campaign is
          still in flight.
        </RecommendationBox>
        <AnswerFooter $visible={phase >= 5}>
          <SourcesRow>
            <SectionLabel>Sources</SectionLabel>
            <SourcePill>
              Campaign report <ExternalLink size={11} />
            </SourcePill>
          </SourcesRow>
          <ActionButton>Open campaign report</ActionButton>
        </AnswerFooter>
      </DemoBody>
    </DemoWindow>
  );
};

const MCP_CLIENTS = ['Claude', 'ChatGPT', 'Cursor', 'Codex', 'Gemini'];

const McpVisual = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  min-height: 0;
`;

const McpChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const McpChip = styled('span')`
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.INK};
  background: ${COLORS.GRAY.surface};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 100px;
  padding: 5px 12px;
`;

const McpFanSvg = styled('svg')`
  @keyframes mcpFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -9;
    }
  }

  display: block;
  width: 100%;
  max-width: 250px;
  height: 44px;
  margin: 2px auto 0;
  overflow: visible;

  path {
    fill: none;
    stroke: rgba(46, 92, 255, 0.35);
    stroke-width: 1.2;
    stroke-dasharray: 3 6;
    animation: mcpFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const McpCore = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 auto;
  padding: 8px 16px;
  border: 1px solid ${COLORS.PRIMARY_BORDER};
  border-radius: 100px;
  background: ${COLORS.PRIMARY_SURFACE};
  box-shadow: 0 4px 16px rgba(46, 92, 255, 0.1);

  img {
    width: 22px;
    height: 22px;
    border-radius: 50%;
  }

  span {
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.INK};
  }
`;

const MCP_FAN_W = 250;
const MCP_FAN_H = 44;
const MCP_FAN_TARGETS = [15, 70, 125, 180, 235];

const mcpFanPath = (targetX: number) => {
  const centerX = MCP_FAN_W / 2;
  const bendY = MCP_FAN_H * 0.55;
  return `M ${centerX} 0 C ${centerX} ${
    bendY * 0.45
  }, ${targetX} ${bendY}, ${targetX} ${MCP_FAN_H}`;
};

const McpConnections = () => (
  <>
    <McpCore>
      <img src="/favicon.png" alt="Affilimate" />
      <span>Your workspace</span>
    </McpCore>
    <McpFanSvg
      viewBox={`0 0 ${MCP_FAN_W} ${MCP_FAN_H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {MCP_FAN_TARGETS.map((x) => (
        <path key={x} d={mcpFanPath(x)} />
      ))}
    </McpFanSvg>
    <McpChips>
      {MCP_CLIENTS.map((client) => (
        <McpChip key={client}>{client}</McpChip>
      ))}
    </McpChips>
  </>
);

/* --------------------------- Section body ------------------------- */

const smallTiles = [
  {
    icon: <Sparkles size={19} />,
    title: 'Campaign execution',
    description:
      'Pitch, flight, and prove sponsorships in a purpose-built system: from approvals to link creation and wrap reports.'
  },
  {
    icon: <Eye size={19} />,
    title: 'Agent analytics',
    description:
      'See how AI assistants crawl your monetized pages, alongside clicks and commissions on the same view.'
  },
  {
    icon: <Zap size={19} />,
    title: 'Yield optimization',
    description:
      'Surface product-level optimization opportunities, curated by AI and ready to deploy in minutes.'
  }
];

const ExploreRow = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 36px;
`;

const ExploreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

export const AiPlatformSection = () => {
  return (
    <SectionWrapper id="ai-platform">
      <Inner>
        <SectionHeader>
          <Reveal>
            <Headline component="h2">
              Not another "chat with your data"
            </Headline>
            <SectionDescription>
              Affilimate&apos;s AI platform is built for the commerce workflows
              your team runs every day. Catch revenue leaks, execute campaigns,
              and optimize yield from the same commerce data.
            </SectionDescription>
          </Reveal>
        </SectionHeader>

        <BentoGrid>
          <HeroCell>
            <HeroTile>
              <TileTitleRow>
                <ProductIcon $size="sm">
                  <MessageCircle size={19} />
                </ProductIcon>
                <TileTitle>Meet Affilimate Agent</TileTitle>
              </TileTitleRow>
              <TileDescription style={{ flex: 'none' }}>
                Catch delivery risk, pitch brands with past performance, and act
                on yield opportunities &mdash; all grounded in centralized
                revenue intelligence that's been vetted by leading publishers
                over 5+ years.
              </TileDescription>
              <AgentDemo />
            </HeroTile>
          </HeroCell>

          <Reveal delay={120}>
            <Tile>
              <TileTitleRow>
                <ProductIcon $size="sm">
                  <Terminal size={19} />
                </ProductIcon>
                <TileTitle>Affilimate MCP</TileTitle>
              </TileTitleRow>
              <TileDescription style={{ flex: 'none' }}>
                Connect AI assistants like Claude and ChatGPT to the same
                commerce data, campaign performance, and yield opportunities
                you'll leverage in the platform interface and APIs.
              </TileDescription>
              <McpVisual>
                <McpConnections />
              </McpVisual>
              <ExploreLink to="/mcp/" style={{ marginTop: 4 }}>
                Learn about Affilimate's MCP <ArrowRight size={16} />
              </ExploreLink>
            </Tile>
          </Reveal>

          {smallTiles.map((tile, index) => (
            <Reveal key={tile.title} delay={index * 100}>
              <Tile>
                <TileTitleRow>
                  <ProductIcon $size="sm">{tile.icon}</ProductIcon>
                  <TileTitle>{tile.title}</TileTitle>
                </TileTitleRow>
                <TileDescription>{tile.description}</TileDescription>
              </Tile>
            </Reveal>
          ))}
        </BentoGrid>

        <Reveal delay={80}>
          <ExploreRow>
            <ExploreLink to="/ai-platform/">
              Explore the AI platform <ArrowRight size={20} />
            </ExploreLink>
          </ExploreRow>
        </Reveal>
      </Inner>
    </SectionWrapper>
  );
};
